
import { createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@/common/bt-auth/auth-service";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import { clearAuthError } from "../reducers/UserReducer";

// Prevent multiple simultaneous initAuth calls
let isInitAuthRunning = false;

/**
 * Initialize authentication on app startup
 */
export const initAuth = createAsyncThunk(
  "initAuth",
  async (_, thunkApi) => {
    if (isInitAuthRunning) {
      console.log('[login-flow] AuthThunks.initAuth: Already running, skipping');
      return { skipped: true };
    }
    
    isInitAuthRunning = true;
    console.log('[login-flow] AuthThunks.initAuth: Starting');
    
    try {
      const dispatch = thunkApi.dispatch as AppThunkDispatch;

      // NOTE: do NOT set 'autoJwtStarted' here. attemptJwtAuth() is the sole owner of the
      // autoJwt* status lifecycle — it sets 'autoJwtStarted' only AFTER its skip-guards
      // (no-token / token-already-attempted), then settles to 'autoJwtSuccess'/'autoJwtFail'.
      // Setting a busy status here meant that when attemptJwtAuth short-circuited on its
      // "token already attempted" guard (e.g. a bt-extension-login re-trigger after a
      // successful login), the status stayed wedged at 'autoJwtStarted' forever and stranded
      // GuardedRoute on the "Authenticating..." loader. Leaving the status untouched here lets
      // a no-op re-trigger preserve the existing success state, while a genuinely new token
      // (account switch) still flows through attemptJwtAuth's full started→success path.
      dispatch(clearAuthError());

      console.log('[login-flow] AuthThunks.initAuth: Attempting JWT authentication...');
      const success = await authService.attemptJwtAuth(dispatch);
      console.log('[login-flow] AuthThunks.initAuth: JWT authentication result =', success);
      
      // Request FCM token after successful authentication (only in extension context)
      if (success) {
        console.log('[login-flow] AuthThunks.initAuth: Authentication successful, checking for FCM token request...');
        try {
          // Check if we can safely call chrome.runtime.sendMessage without an extension ID
          // chrome.runtime.id exists in extension contexts (content script, background, popup, etc)
          const canSendMessageWithoutExtensionId = typeof window !== 'undefined' && 
                                                   typeof chrome !== 'undefined' && 
                                                   chrome?.runtime?.id;
          
          if (canSendMessageWithoutExtensionId) {
            console.log('[login-flow] AuthThunks.initAuth: Extension context detected, requesting FCM token...');
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('blueticks-request-fcm-token'));
            }, 1000);
          } else {
            console.log('[login-flow] AuthThunks.initAuth: Not in extension context, skipping FCM token request');
          }
        } catch (error) {
          console.error('[login-flow] AuthThunks.initAuth: Error dispatching FCM token request event:', error);
        }
      }
      
      return { success };
    } finally {
      isInitAuthRunning = false;
      console.log('[login-flow] AuthThunks.initAuth: Completed');
    }
  }
);

/**
 * Handle external access token (for OAuth flows)
 */
export const externalAccessToken = createAsyncThunk(
  "externalAccessToken",
  async (accessToken: string, thunkApi) => {
    const dispatch = thunkApi.dispatch as AppThunkDispatch;
    
    // Persist the provided token for cross-context availability
    authService.persistToken(accessToken);
    // Avoid immediate auth attempt here to prevent rapid loops; let guarded routes/initAuth drive it
    const success = true;
    
    return success;
  }
);
